<script setup lang="ts">
import { ref, toRefs, computed } from 'vue';
import { DialogModal, AxialForm } from '@common';
import { type ParcelGroupModalProps } from '../../models/parcel-group-modal.model';
import ParcelListElement from './ParcelListElement.vue';
import { ParcelListElementStatus } from '../../enums/parcel-list-element-status.enum';
import GroupListElement from './GroupListElement.vue';
import { useParcelGroupModal } from '../../composables/parcel-group-modal.composable';
import { getParcelStatus, getSelectedArea } from '../../utils/parcel.utils';
import { useModulesConfigStore } from '../../../../store/modules-config.store';

const props = defineProps<ParcelGroupModalProps>();
const { config } = toRefs(props);
const moduleConfigStore = useModulesConfigStore();

const {
  selectGroup,
  selectParcel,
  unselectParcel,
  selectedParcels,
  selectAll,
  unselectAll,
  filteredOffers,
  selectedGroup,
  filteredParcelGroups,
  onConfirm,
  onCancel,
  groupForm,
  filterForm,
  isHideGroupForm
} = useParcelGroupModal(props.config);

const currentTab = ref('offers');

const getParcelListElementStatus = (parcel) => {
  return getParcelStatus(selectedParcels.value, parcel);
};

const selectedArea = computed(() => getSelectedArea(selectedParcels.value));

const confirmBtnName = computed(
  () =>
    config.value.confirmBtnLabel ||
    (config.value.selectedGroup
      ? 'parcel.dialog.editParcelGroup.editGroup'
      : 'parcel.dialog.newParcelGroup.createGroup')
);
</script>

<template>
  <dialog-modal
    :config="config"
    class="parcel-group-modal">
    <template #title></template>
    <template #default>
      <div class="mb-4">
        <p class="mb-2">{{ $t('parcel.dialog.newParcelGroup.filter') }}</p>
        <axial-form :form="filterForm.element"></axial-form>
      </div>
      <v-row>
        <v-col
          cols="12"
          md="4"
          class="pa-1"
          ><v-card>
            <v-tabs
              v-model="currentTab"
              direction="horizontal"
              color="primary">
              <v-tab
                variant="elevated"
                height="44px"
                rounded="0"
                value="offers"
                :width="moduleConfigStore.moduleConfig.parcel.needParcelGroups ? '50%' : '100%'"
                :exact="true">
                {{ $t('parcel.dialog.newParcelGroup.offers.title') }}
              </v-tab>
              <v-tab
                v-if="moduleConfigStore.moduleConfig.parcel.needParcelGroups"
                height="44px"
                rounded="0"
                variant="elevated"
                value="groups"
                width="50%"
                :exact="true">
                {{ $t('parcel.dialog.newParcelGroup.groups.title') }}
              </v-tab></v-tabs
            >

            <v-window v-model="currentTab">
              <v-window-item value="offers">
                <group-list-element
                  v-for="(offer, i) in filteredOffers"
                  :key="i"
                  class="group-element"
                  :group="offer"
                  :selected="offer === selectedGroup"
                  @click="selectGroup(offer)">
                </group-list-element>
              </v-window-item>

              <v-window-item value="groups">
                <group-list-element
                  v-for="(group, i) in filteredParcelGroups"
                  :key="i"
                  class="group-element"
                  :group="group"
                  :selected="group === selectedGroup"
                  @click="selectGroup(group)">
                </group-list-element>
              </v-window-item>
            </v-window>
          </v-card>
        </v-col>
        <v-col
          class="pa-1"
          cols="12"
          md="4"
          ><v-card class="h-100">
            <h3 class="text-center py-2 bg-primary">{{ $t('parcel.dialog.newParcelGroup.parcels.title') }}</h3>
            <div class="h-100 overflow-auto-lighten-3">
              <div class="d-flex">
                <v-btn
                  v-if="selectedGroup && selectedGroup.parcels.length"
                  size="small"
                  color="primary-lighten-2"
                  class="d-flex flex-grow-1 mx-2 mt-2"
                  append-icon="mdi-check-circle-outline"
                  @click="selectAll"
                  >{{ $t('parcel.dialog.newParcelGroup.parcels.selectAll') }}</v-btn
                >
              </div>
              <template v-if="selectedGroup">
                <parcel-list-element
                  v-for="(parcel, i) in selectedGroup.parcels"
                  class="parcel-element"
                  :key="i"
                  :parcel="parcel"
                  :status="getParcelListElementStatus(parcel)"
                  :show-heightmap-coverage="config.showHeightmapCoverage"
                  :incline-layers-blocked="config.inclineLayersBlocked"
                  :block-without-valid-zoning="config.blockWithoutValidZoning"
                  @select-parcel="selectParcel(parcel)">
                </parcel-list-element>
              </template>
            </div>
          </v-card>
        </v-col>
        <v-col
          class="pa-1"
          cols="12"
          md="4">
          <v-card class="h-100">
            <h3 class="text-center py-2 bg-primary">{{ $t('parcel.dialog.newParcelGroup.selectedParcels.title') }}</h3>
            <div class="d-flex justify-space-around bg-primary-lighter py-1 font-weight-bold">
              <template v-if="config.maxSelectableParcels">
                <p>
                  {{ selectedParcels.length }} / {{ config.maxSelectableParcels }}
                  {{ $t('parcel.dialog.newParcelGroup.selectedParcels.parcelSelected') }}
                </p>
              </template>
              <template v-else>
                <p>{{ selectedParcels.length }} {{ $t('parcel.dialog.newParcelGroup.selectedParcels.parcel') }}</p>
                <p>{{ selectedArea }} {{ $t('parcel.dialog.newParcelGroup.selectedParcels.hectare') }}</p>
              </template>
            </div>
            <div class="h-100 overflow-auto">
              <div class="d-flex">
                <v-btn
                  v-if="selectedParcels.length"
                  size="small"
                  color="error"
                  class="d-flex flex-grow-1 mx-2 mt-2"
                  append-icon="mdi-delete"
                  @click="unselectAll"
                  >{{ $t('parcel.dialog.newParcelGroup.selectedParcels.deleteAll') }}</v-btn
                >
              </div>
              <parcel-list-element
                v-for="(parcel, i) in selectedParcels"
                class="parcel-element"
                :key="i"
                :parcel="parcel"
                :status="ParcelListElementStatus.SELECTED"
                @unselect-parcel="unselectParcel(parcel)">
              </parcel-list-element>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
    <template #actions="{ close }">
      <div class="d-flex flex-grow-1 justify-space-between">
        <div class="flex-grow-1 mr-2">
          <axial-form
            v-if="!isHideGroupForm"
            :form="groupForm.element"></axial-form>
        </div>
        <div class="mt-1">
          <v-btn
            variant="elevated"
            class="mx-2 px-3"
            color="primary"
            @click="onConfirm(close)">
            {{ $t(confirmBtnName) }}
          </v-btn>
          <v-btn
            class="px-3 mr-3"
            color="error"
            @click="onCancel(close)">
            {{ $t('button.cancel') }}
          </v-btn>
        </div>
      </div>
    </template>
  </dialog-modal>
</template>
